/**
 * CreateVersionModal (NQG-RQ-27)
 *
 * Creates a new version of a generated question along two independent axes:
 *  - Horizontal (category): rendered dynamically from GET /version-config (AC-4)
 *  - Vertical (difficulty): facil | medio | dificil (also from config)
 * Both axes are optional but at least one is required. Categories with
 * requiresUserPrompt=true (e.g. "Conceito") need an extra concept prompt.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { GitBranch, Loader2, X, AlertCircle, CheckCircle, Gauge } from 'lucide-react';
import Swal from 'sweetalert2';
import { pdfAnalysisService } from '../../services/pdf-analysis.service';
import { useAuthStore } from '../../store/authStore';
import type { VersionCategory } from '../../types/pdf-analysis.types';

interface CreateVersionModalProps {
  /** The original question to create a version of (needs _id, stem, oeCode) */
  question: any;
  onClose: () => void;
  /** Called with the newly created version document */
  onCreated?: (version: any) => void;
}

export function CreateVersionModal({ question, onClose, onCreated }: CreateVersionModalProps) {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();

  // Form state — both axes optional, at least one required
  const [category, setCategory] = useState<string | null>(null);
  const [difficulty, setDifficulty] = useState<string | null>(null);
  const [conceptPrompt, setConceptPrompt] = useState('');
  const [description, setDescription] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  // Config-based category/difficulty list (AC-4 — never hardcoded)
  const {
    data: config,
    isLoading: configLoading,
    error: configError,
  } = useQuery({
    queryKey: ['version-config'],
    queryFn: () => pdfAnalysisService.getVersionConfig(),
    staleTime: 5 * 60 * 1000,
  });

  const selectedCategory: VersionCategory | undefined = config?.categories.find(
    (c) => c.key === category
  );
  const needsPrompt = !!selectedCategory?.requiresUserPrompt;

  const createMutation = useMutation({
    mutationFn: () =>
      pdfAnalysisService.createQuestionVersion(question._id, {
        category: category || undefined,
        difficulty: difficulty || undefined,
        conceptPrompt: needsPrompt ? conceptPrompt.trim() : undefined,
        description: description.trim() || undefined,
        createdBy: user?.name || user?.email || undefined,
      }),
    onSuccess: (version) => {
      queryClient.invalidateQueries({ queryKey: ['pdf-generated-questions'] });
      queryClient.invalidateQueries({ queryKey: ['question-versions'] });
      Swal.fire({
        title: 'Version Created!',
        text: 'The new question version was generated successfully.',
        icon: 'success',
        timer: 2000,
        showConfirmButton: false,
      });
      onCreated?.(version);
    },
    onError: (err: any) => {
      Swal.fire({
        title: 'Version Failed',
        text: err.response?.data?.error || err.message || 'Failed to create question version.',
        icon: 'error',
        confirmButtonColor: '#dc2626',
      });
    },
  });

  const isPending = createMutation.isPending;

  const toggleCategory = (key: string) => {
    if (isPending) return;
    setCategory((prev) => (prev === key ? null : key));
    setValidationError(null);
  };

  const toggleDifficulty = (key: string) => {
    if (isPending) return;
    setDifficulty((prev) => (prev === key ? null : key));
    setValidationError(null);
  };

  const handleSubmit = () => {
    if (!category && !difficulty) {
      setValidationError('Select at least one axis: a category or a difficulty.');
      return;
    }
    if (needsPrompt && !conceptPrompt.trim()) {
      setValidationError(
        `The "${selectedCategory?.label}" category requires the concept and format/pattern prompt.`
      );
      return;
    }
    setValidationError(null);
    createMutation.mutate();
  };

  const handleClose = () => {
    if (isPending) return; // don't close while generating
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col overflow-hidden animate-in">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#1e73be] to-[#4A90C8] px-6 py-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-xl">
                <GitBranch className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">Create Version</h2>
                <div className="flex items-center gap-2 mt-0.5">
                  {question.oeCode && (
                    <span className="text-xs font-mono font-bold text-white/80 bg-white/20 px-2 py-0.5 rounded">
                      {question.oeCode}
                    </span>
                  )}
                  <span className="text-xs text-white/60 truncate max-w-[320px]">
                    {(question.stem || '').substring(0, 80)}
                  </span>
                </div>
              </div>
            </div>
            <button
              onClick={handleClose}
              disabled={isPending}
              className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-xl transition-all disabled:opacity-40"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5 custom-scrollbar">
          {configLoading ? (
            <div className="flex items-center justify-center py-12 gap-3 text-gray-500">
              <Loader2 className="w-5 h-5 animate-spin text-[#1e73be]" />
              <span className="text-sm font-medium">Loading version options...</span>
            </div>
          ) : configError || !config ? (
            <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              Failed to load version configuration. Please try again.
            </div>
          ) : (
            <>
              {/* Category (horizontal axis) — rendered dynamically from config */}
              <div>
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                  Category <span className="font-medium text-gray-400 normal-case">(optional)</span>
                </p>
                <div className="space-y-2">
                  {config.categories.map((cat) => {
                    const selected = category === cat.key;
                    return (
                      <button
                        key={cat.key}
                        type="button"
                        onClick={() => toggleCategory(cat.key)}
                        disabled={isPending}
                        className={`w-full text-left p-4 rounded-xl border-2 transition-all disabled:opacity-60 ${
                          selected
                            ? 'border-[#1e73be] bg-blue-50 ring-2 ring-blue-200'
                            : 'border-gray-200 hover:border-blue-300 bg-white'
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div
                            className={`mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                              selected ? 'border-[#1e73be] bg-[#1e73be]' : 'border-gray-300'
                            }`}
                          >
                            {selected && <CheckCircle className="w-4 h-4 text-white" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-bold text-gray-900">{cat.label}</span>
                              {cat.requiresUserPrompt && (
                                <span className="text-[10px] font-bold uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">
                                  Requires prompt
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                              {cat.description}
                            </p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Difficulty (vertical axis) — rendered dynamically from config */}
              <div>
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                  <Gauge className="w-3.5 h-3.5 text-gray-400" />
                  Difficulty <span className="font-medium text-gray-400 normal-case">(optional)</span>
                </p>
                <div className="flex flex-wrap gap-2">
                  {config.difficulties.map((d) => {
                    const selected = difficulty === d.key;
                    return (
                      <button
                        key={d.key}
                        type="button"
                        onClick={() => toggleDifficulty(d.key)}
                        disabled={isPending}
                        className={`px-4 py-2 rounded-xl text-sm font-semibold border-2 transition-all disabled:opacity-60 ${
                          selected
                            ? 'border-[#1e73be] bg-blue-50 text-[#1e73be] ring-2 ring-blue-200'
                            : 'border-gray-200 text-gray-700 hover:border-blue-300 bg-white'
                        }`}
                      >
                        {d.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Concept prompt — required when the category demands it */}
              {needsPrompt && (
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                    Concept and format/pattern <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={conceptPrompt}
                    onChange={(e) => {
                      setConceptPrompt(e.target.value);
                      setValidationError(null);
                    }}
                    disabled={isPending}
                    rows={4}
                    placeholder="Describe the concept to be tested and the desired question format/pattern..."
                    className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#1e73be] focus:ring-2 focus:ring-blue-100 transition-all placeholder-gray-400 disabled:bg-gray-50"
                  />
                </div>
              )}

              {/* Optional description (shown in version history) */}
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                  Description <span className="font-medium text-gray-400 normal-case">(optional)</span>
                </label>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  disabled={isPending}
                  maxLength={500}
                  placeholder="Short note shown in the version history..."
                  className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[#1e73be] focus:ring-2 focus:ring-blue-100 transition-all placeholder-gray-400 disabled:bg-gray-50"
                />
              </div>

              {/* Validation error */}
              {validationError && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {validationError}
                </div>
              )}

              {/* Generation in progress */}
              {isPending && (
                <div className="flex items-center gap-3 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                  <Loader2 className="w-5 h-5 animate-spin text-[#1e73be] flex-shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-blue-800">Generating version...</p>
                    <p className="text-xs text-blue-600 mt-0.5">
                      The AI is generating the new question. This can take up to a minute.
                    </p>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/50 flex-shrink-0">
          <button
            onClick={handleClose}
            disabled={isPending}
            className="btn-secondary flex-1 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isPending || configLoading || !config}
            className="btn-primary flex-1 py-2.5 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Generating version...
              </>
            ) : (
              <>
                <GitBranch className="w-4 h-4" />
                Generate Version
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CreateVersionModal;
